import {
  depositAssetRequiredForVaultToken,
  vaultTokenReceivedForDepositAsset,
  withdrawAssetReceivedForVaultToken,
  vaultTokenRequiredForAsset,
} from '@/components/YearnAeraVaultWidget/yearnConversions'
import { YearnBalances } from '@/state/yearnVault/types'
import { EthInput, SwEthInput } from '@/swell-ui/inputs'
import { ThemeData } from '@/swell-ui/theme/branding'
import { Token } from '@/types/tokens'
import { YearnWithdrawAsset } from '@/types/yearnAera'
import { trimDecimalPlaces } from '@/util/number'
import { BigNumber } from 'ethers'
import { parseUnits, formatUnits } from 'ethers/lib/utils'
import styled, { css } from 'styled-components'

const StyledEthInput = styled(EthInput)`
  ${({ theme }) => css`
    .MuiFormHelperText-root {
      margin-top: 2px;
      margin-bottom: 16px;
    }

    ${theme.breakpoints.up('sm')} {
      max-width: unset;
    }
  `}
  .MuiInputBase-root:after {
    /* border-color: ${(props) => props.theme.input.underlineFocusColor}; */
  }
`
const StyledSwEthInput = styled(SwEthInput)`
  .MuiInputBase-root:after {
    /* border-color: ${(props) => props.theme.input.underlineFocusColor}; */
  }
`

// Deposit: top input
export function DepositAssetInput({
  errorMessage,
  setDestTokenInputValue,
  setSrcTokenInputValue,
  srcTokenInputValue,
  pricePerShare,
  balances,
  disabled,
  vaultToken,
  depositAsset,
  setTouched,
  account,
}: {
  setTouched: (touched: boolean) => void
  srcTokenInputValue: string
  setSrcTokenInputValue: (value: string) => void
  setDestTokenInputValue: (value: string) => void
  errorMessage: string | null
  pricePerShare: BigNumber | undefined
  balances: YearnBalances | undefined
  disabled: boolean
  vaultToken: Token
  depositAsset: Token
  account: string | undefined
}) {
  const onChange = (event: any) => {
    if (!pricePerShare) {
      console.error('No pricePerShare')
      return
    }

    const value = event.target.value
    if (value === '') {
      setSrcTokenInputValue('')
      setDestTokenInputValue('')
      return
    }

    setTouched(true)

    const decimalPlaces = depositAsset.decimals
    const valueClean = trimDecimalPlaces(value, decimalPlaces)
    setSrcTokenInputValue(valueClean)

    const depositAssetAmount = parseUnits(valueClean, depositAsset.decimals)
    const receiveVaultTokenAmount = vaultTokenReceivedForDepositAsset({
      toSendAsset: depositAssetAmount,
      pricePerShare,
      vaultTokenDecimals: vaultToken.decimals,
    })
    const destAmountStr = formatUnits(
      receiveVaultTokenAmount,
      vaultToken.decimals
    )
    setDestTokenInputValue(destAmountStr)
  }

  const handleMaxClick = () => {
    if (!balances) return
    onChange({
      target: {
        value: formatUnits(balances.depositAsset, depositAsset.decimals),
      },
    })
  }

  const accountAndNoBalance = !balances && !!account

  return (
    <StyledEthInput
      variant="standard"
      value={srcTokenInputValue}
      onChange={onChange}
      error={!!errorMessage}
      helperText={errorMessage}
      disabled={disabled || !pricePerShare || accountAndNoBalance}
      onMaxClick={handleMaxClick}
    />
  )
}

// Deposit: bottom input
export function DepositReceiveVaultTokenInput({
  setDestTokenInputValue,
  setSrcTokenInputValue,
  setTouched,
  destTokenInputValue,
  pricePerShare,
  disabled,
  depositAsset,
  vaultToken,
  balances,
  account,
}: {
  setTouched: (touched: boolean) => void
  destTokenInputValue: string
  setSrcTokenInputValue: (value: string) => void
  setDestTokenInputValue: (value: string) => void
  pricePerShare: BigNumber | undefined
  disabled: boolean
  vaultToken: Token
  depositAsset: Token
  balances: YearnBalances | undefined
  account: string | undefined
}) {
  const onChange = (event: any) => {
    if (!pricePerShare) {
      console.error('No rates')
      return
    }

    const value = event.target.value
    if (value === '') {
      setSrcTokenInputValue('')
      setDestTokenInputValue('')
      return
    }

    setTouched(true)

    const decimalPlaces = vaultToken.decimals
    const valueClean = trimDecimalPlaces(value, decimalPlaces)
    setDestTokenInputValue(valueClean)

    const toReceiveVaultToken = parseUnits(valueClean, vaultToken.decimals)
    const depositAssetAmount = depositAssetRequiredForVaultToken({
      toReceiveVaultToken,
      pricePerShare,
      vaultTokenDecimals: vaultToken.decimals,
    })
    const srcAmountStr = formatUnits(depositAssetAmount, depositAsset.decimals)
    setSrcTokenInputValue(srcAmountStr)
  }

  const accountAndNoBalance = !balances && !!account

  return (
    <StyledSwEthInput
      variant="standard"
      value={destTokenInputValue}
      onChange={onChange}
      disabled={disabled || !pricePerShare || accountAndNoBalance}
    />
  )
}

// Withdraw: top input
export function WithdrawVaultTokenInput({
  errorMessage,
  setDestTokenInputValue,
  setSrcTokenInputValue,
  setTouched,
  srcTokenInputValue,
  balances,
  disabled,
  maxLossBasisPoints,
  vaultToken,
  withdrawAsset,
  pricePerShare,
  account,
}: {
  setTouched: (touched: boolean) => void
  srcTokenInputValue: string
  setSrcTokenInputValue: (value: string) => void
  setDestTokenInputValue: (value: string) => void
  errorMessage: string | null
  pricePerShare: BigNumber | undefined
  balances: YearnBalances | undefined
  disabled: boolean
  vaultToken: Token
  withdrawAsset: YearnWithdrawAsset | undefined
  maxLossBasisPoints: number | undefined
  account: string | undefined
}) {
  const onChange = (event: any) => {
    if (!pricePerShare) {
      console.error('No withdraw rates')
      return
    }
    if (typeof maxLossBasisPoints !== 'number') {
      console.error('No max loss basis points')
      return
    }
    if (!withdrawAsset) {
      console.error('No withdraw asset')
      return
    }

    const value = event.target.value
    if (value === '') {
      setSrcTokenInputValue('')
      setDestTokenInputValue('')
      return
    }

    setTouched(true)

    const decimalPlaces = vaultToken.decimals
    const valueClean = trimDecimalPlaces(value, decimalPlaces)
    setSrcTokenInputValue(valueClean)

    const toSendVaultToken = parseUnits(valueClean, vaultToken.decimals)
    const receivedAssets = withdrawAssetReceivedForVaultToken({
      pricePerShare,
      toSendVaultToken: toSendVaultToken,
      withdrawFeeBasisPoints: withdrawAsset.withdrawFeeBasisPoints,
      vaultTokenDecimals: vaultToken.decimals,
    })
    const destAmountStr = formatUnits(receivedAssets, withdrawAsset.decimals)
    setDestTokenInputValue(destAmountStr)
  }

  const handleMaxClick = () => {
    if (!balances) return
    onChange({
      target: {
        value: formatUnits(balances.vaultToken, vaultToken.decimals),
      },
    })
  }

  const accountAndNoBalance = !balances && !!account

  return (
    <StyledEthInput
      variant="standard"
      value={srcTokenInputValue}
      onChange={onChange}
      error={!!errorMessage}
      helperText={errorMessage}
      disabled={
        disabled || !pricePerShare || accountAndNoBalance || !withdrawAsset
      }
      onMaxClick={handleMaxClick}
    />
  )
}

// Withdraw: bottom input
export function WithdrawReceiveAssetInput({
  setDestTokenInputValue,
  setSrcTokenInputValue,
  destTokenInputValue,
  disabled,
  setTouched,
  vaultToken,
  withdrawAsset,
  pricePerShare,
  balances,
  account,
}: {
  setTouched: (touched: boolean) => void
  destTokenInputValue: string
  setSrcTokenInputValue: (value: string) => void
  setDestTokenInputValue: (value: string) => void
  pricePerShare: BigNumber | undefined
  disabled: boolean
  withdrawAsset: YearnWithdrawAsset | undefined
  vaultToken: Token
  balances: YearnBalances | undefined
  account: string | undefined
}) {
  const onChange = (event: any) => {
    if (!pricePerShare) {
      console.error('No withdraw rates')
      return
    }
    if (!withdrawAsset) {
      console.error('No withdraw asset')
      return
    }
    const value = event.target.value
    if (value === '') {
      setSrcTokenInputValue('')
      setDestTokenInputValue('')
      return
    }

    setTouched(true)

    const decimalPlaces = withdrawAsset.decimals
    const valueClean = trimDecimalPlaces(value, decimalPlaces)
    setDestTokenInputValue(valueClean)

    const toReceiveAsset = parseUnits(valueClean, withdrawAsset.decimals)
    const vaultTokenRequired = vaultTokenRequiredForAsset({
      pricePerShare,
      toReceiveAsset,
      withdrawFeeBasisPoints: withdrawAsset.withdrawFeeBasisPoints,
      vaultTokenDecimals: vaultToken.decimals,
    })
    const srcAmountStr = formatUnits(vaultTokenRequired, vaultToken.decimals)
    setSrcTokenInputValue(srcAmountStr)
  }

  const accountAndNoBalance = !balances && !!account

  return (
    <StyledSwEthInput
      variant="standard"
      value={destTokenInputValue}
      onChange={onChange}
      disabled={
        disabled || !pricePerShare || accountAndNoBalance || !withdrawAsset
      }
    />
  )
}
